/* Browser voice helpers: comic/childlike TTS + speech recognition (with graceful fallbacks). */

let cachedVoices = [];
const loadVoices = () =>
  new Promise((resolve) => {
    const v = window.speechSynthesis?.getVoices() || [];
    if (v.length) return resolve(v);
    window.speechSynthesis?.addEventListener("voiceschanged", () => resolve(window.speechSynthesis.getVoices()), { once: true });
    setTimeout(() => resolve(window.speechSynthesis?.getVoices() || []), 1200);
  });

const pickVoice = (voices, lang) => {
  const pref = lang === "es" ? ["es-MX", "es-US", "es-ES", "es"] : ["en-US", "en-GB", "en"];
  for (const p of pref) {
    const found = voices.find((v) => v.lang?.toLowerCase().startsWith(p.toLowerCase()));
    if (found) return found;
  }
  return voices[0] || null;
};

export const ttsAvailable = () => typeof window !== "undefined" && "speechSynthesis" in window;

/** Speaks text with a natural, warm human-like delivery. Resolves when done.
 *  WATCHDOG: mobile browsers may block/stall speech without a user gesture —
 *  this promise ALWAYS resolves within a bounded time so UI flows never hang. */
export const speak = (text, lang = "es", { pitch = 1.0, rate = 1.0 } = {}) =>
  new Promise(async (resolve) => {
    if (!ttsAvailable() || !text) return resolve(false);
    let settled = false;
    const done = (v) => { if (!settled) { settled = true; resolve(v); } };
    const watchdog = setTimeout(() => done(false), Math.min(14000, 2500 + text.length * 65));
    try {
      window.speechSynthesis.cancel();
      if (!cachedVoices.length) cachedVoices = await loadVoices();
      const u = new SpeechSynthesisUtterance(text);
      const voice = pickVoice(cachedVoices, lang);
      if (voice) u.voice = voice;
      u.lang = lang === "es" ? "es-MX" : "en-US";
      u.pitch = pitch; // 1.0 = natural human tone
      u.rate = rate;
      u.onend = () => { clearTimeout(watchdog); done(true); };
      u.onerror = () => { clearTimeout(watchdog); done(false); };
      window.speechSynthesis.speak(u);
      try { window.speechSynthesis.resume(); } catch { /* Chrome pause quirk */ }
    } catch {
      clearTimeout(watchdog);
      done(false);
    }
  });

export const stopSpeaking = () => {
  try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
};

export const sttAvailable = () =>
  typeof window !== "undefined" && ("webkitSpeechRecognition" in window || "SpeechRecognition" in window);

/** Listens once and resolves with the transcript (or null on failure/silence). */
export const listen = (lang = "es", timeoutMs = 12000) =>
  new Promise((resolve) => {
    if (!sttAvailable()) return resolve(null);
    try {
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      const rec = new SR();
      rec.lang = lang === "es" ? "es-MX" : "en-US";
      rec.interimResults = false;
      rec.maxAlternatives = 1;
      let done = false;
      const finish = (val) => { if (!done) { done = true; try { rec.stop(); } catch { /* noop */ } resolve(val); } };
      rec.onresult = (e) => finish(e.results?.[0]?.[0]?.transcript || null);
      rec.onerror = () => finish(null);
      rec.onend = () => finish(null);
      rec.start();
      setTimeout(() => finish(null), timeoutMs);
    } catch {
      resolve(null);
    }
  });
